// @givmo/app-charity — the auth-backend transport adapter (OTP subset).
//
// The charity portal reuses @givmo/auth's backend-OTP email-verification flow, which talks to the
// GENERIC `/users/me/email_verification` + `/users/me/confirm_email` endpoints (NOT company- or
// charity-specific). @givmo/auth's OTP wrappers (`adminSendVerificationEmail` /
// `adminConfirmEmailCode`) are typed against the full `AdminAuthBackend` port even though they only
// call its two OTP methods, so this adapter implements those two for real over the generated SDK and
// leaves the COMPANY-registration methods unimplemented.
//
// Why the company methods throw: charity ORG creation is the claim flow (CPA-WEB2 against CPA-BE2),
// NOT company registration — a charity admin is a Firebase user with charity memberships, provisioned
// by claim approval, so this scaffold never calls `registerCompany` / `getCompany` /
// `checkEmailAvailable`, nor `provisionUser` (the employer email sign-up's row bootstrap, NF-FIX21).
// Guarding them makes any accidental future call loud instead of silently hitting the
// employer-company surface. Replace with real `/charity-admin/*` calls when they exist.
//
// ★ THE OTP SEND CARRIES A SURFACE HINT (CPA-BE30). The shared `/users/me/email_verification` route
// used to have no way of knowing WHICH product was asking, so the charity portal's verification mail
// arrived on the COMPANY template ("Givmo Link Company: …", "linking to your company", "Hello, !"
// from an empty display name — Jonathan's 2026-09-11 sign-up). The backend now accepts an ADDITIVE,
// OPTIONAL `?surface=charity_portal` and picks the charity template — its id is baked as the backend's
// default (created in SendGrid 2026-09-11); the company template is sent only if that id is overridden
// to "" per environment (the safety net). This adapter — and ONLY this adapter — sends it; the employer
// portal's `admin-backend.ts` and every other `@givmo/auth` consumer keep the bare send. The
// committed OpenAPI snapshot predates the parameter (the generated op's `query` is typed `never`),
// so the send goes through the configured client's own `post` — the sanctioned hatch for a route
// shape the snapshot lacks (`agent_docs/api-client.md`); graduate to `sdk.usersSendEmailVerification({
// query })` once a regen absorbs the parameter.

import { givmoClient, sdk } from '@givmo/api-client';
import type { AdminAuthBackend } from '@givmo/auth';
import type { Auth } from 'firebase/auth';

/** The route the shared OTP send lives on — the generated op's own `url`. */
export const EMAIL_VERIFICATION_PATH = '/users/me/email_verification';

/**
 * The surface hint this portal's OTP send carries (`CHARITY_PORTAL_SURFACE` on the backend,
 * `app/domain/value_objects.py`): the ONE value the route's `?surface=` enum accepts.
 */
export const CHARITY_PORTAL_SURFACE = 'charity_portal';

/**
 * Throw the generated op's `error` when the call failed, else return its `data`. The generated SDK
 * returns `{ data, error }` (it does NOT throw by default). Throwing on `error` is what drives the
 * @givmo/auth OTP-confirm failure branch; the thrown value is the api-client `GivmoError`.
 */
function unwrap<T>(result: { data?: T; error?: unknown }): T | undefined {
  if (result.error !== undefined && result.error !== null) {
    throw result.error;
  }
  return result.data;
}

/** The methods this scaffold does not use (charity org lifecycle is the claim flow, not registration). */
function notInScaffold(method: string): never {
  throw new Error(
    `${method} is not wired in the charity portal scaffold (CPA-WEB1). ` +
      `Charity org lifecycle is the claim flow (CPA-WEB2 / CPA-BE2), not company registration.`,
  );
}

/**
 * Build the {@link AdminAuthBackend} the charity auth flows consume. Only the backend-OTP methods are
 * implemented (wired to the generated SDK); the company-registration methods throw because the
 * charity scaffold never drives them.
 */
export function createCharityAuthBackend(auth: Auth): AdminAuthBackend {
  return {
    // POST /users/me/email_verification?surface=charity_portal — trigger the OTP email on the
    // charity template (CPA-BE30; see the header). Success is a documented `200` with a JSON `null`
    // body, so this is error-only, NOT `unwrap`: a bare unwrap would false-throw on the happy path,
    // which the verify screen's mount-send would surface as an error before the caller typed anything.
    async sendVerificationEmail() {
      const { error } = await givmoClient.post({
        url: EMAIL_VERIFICATION_PATH,
        query: { surface: CHARITY_PORTAL_SURFACE },
      });
      if (error !== undefined && error !== null) throw error;
    },

    // POST /users/me/confirm_email?verification_code=… — confirm the OTP (query param, NOT body).
    // Throws on an invalid code (→ @givmo/auth surfaces the wrong-code error).
    async confirmEmailCode(code: string) {
      unwrap(await sdk.usersConfirmUserEmail({ query: { verification_code: code } }));
      const user = auth.currentUser;
      if (user === null) {
        throw new Error('Email verification requires a signed-in user.');
      }
      await user.reload();
      await user.getIdTokenResult(true);
    },

    // ── Company-registration surface — unused in the charity scaffold (see file header). ──────────
    provisionUser() {
      return notInScaffold('provisionUser');
    },
    checkEmailAvailable() {
      return notInScaffold('checkEmailAvailable');
    },
    registerCompany() {
      return notInScaffold('registerCompany');
    },
    resolveDomain() {
      return notInScaffold('resolveDomain');
    },
    getCompany() {
      return notInScaffold('getCompany');
    },
    isEmailVerified() {
      return notInScaffold('isEmailVerified');
    },
  };
}
